/**
 * @fileoverview Componente para mostrar indicador de producto agotado
 * @module ProductOutOfStock
 * @category Component - Atom
 * @since 1.0.0
 * @version 1.0.0
 */
import React, { memo } from 'react';
import { useTranslation } from 'react-i18next';

/**
 * @typedef {Object} Product
 * @description Objeto producto con información de stock
 * @property {boolean} is_out_of_stock - Indica si el producto está agotado
 */

/**
 * Componente Atom para mostrar indicador de producto agotado.
 *
 * Implementa el patrón **Atom** de Atomic Design como elemento
 * básico para indicar estado de stock del producto.
 *
 * @component ProductOutOfStock
 * @category Component - Atom
 *
 * @description
 * **Características:**
 * - Indicador visual de producto agotado
 * - Posicionamiento absoluto sobre imagen del producto
 * - Estilos Tailwind CSS migrados del original
 * - Texto internacionalizable
 * - Optimizado con React.memo
 *
 * @param {Object} props - Propiedades del componente
 * @param {Product} props.product - Producto a verificar stock
 *
 * @returns {JSX.Element|false} Elemento JSX con indicador o false si hay stock
 *
 * @example
 * ```jsx
 * <ProductOutOfStock product={productData} />
 * ```
 */
const ProductOutOfStock = memo(({ product }) => {
  const { t } = useTranslation('Inbox');

  const { is_out_of_stock } = product;

  if (!is_out_of_stock) {
    return false;
  }

  return (
    <div
      className="tw-absolute tw-bg-red-200 tw-border tw-border-red-200 tw-px-1.5 tw-py-0.5 tw-rounded-sm"
      style={{ right: 8, top: 5 }}
    >
      <span className="tw-font-medium tw-text-xs tw-text-red-700">
        {t('products.outOfStock', 'Agotado')}
      </span>
    </div>
  );
});

/**
 * Nombre de display para debugging y herramientas de desarrollo.
 *
 * @type {string}
 */
ProductOutOfStock.displayName = 'ProductOutOfStock';

export default ProductOutOfStock;
